// Modified by Sullivan Digital, 2026-10-26: the OSS selectors read the SALDP white-labelling settings (brand name, Metabase links), with no Metabase illustrations and neutral loading copy. Licensed under the GNU AGPL v3.
import type { Action, Middleware, ThunkDispatch } from "@reduxjs/toolkit";
import type { ComponentType, ReactNode } from "react";
import { t } from "ttag";

import noResultsSource from "assets/img/no_results.svg";
import type { AdminPathKey, State } from "metabase/redux/store";
import {
  getBrandName,
  getShowMetabaseLinks,
} from "metabase/saldp-branding/selectors";
import type { UiParameter } from "metabase-lib/v1/parameters/types";
import type { Dashboard, DraftDashboardSubscription } from "metabase-types/api";

import { definePluginSlot } from "../slot";

import type {
  SnippetSidebarContext,
  SnippetSidebarMenuOption,
  SnippetSidebarRowRenderers,
} from "./snippets";

// Types
export type IllustrationValue = {
  src: string;
  isDefault: boolean;
} | null;

interface PluginDashboardSubscriptionParametersSectionOverride {
  Component?: ComponentType<{
    className?: string;
    parameters: UiParameter[];
    hiddenParameters?: string;
    dashboard: Dashboard;
    pulse: DraftDashboardSubscription;
    setPulseParameters: (parameters: UiParameter[]) => void;
  }>;
}

// SALDP: neutral loading copy.
const getLoadingMessage = (isSlow: boolean | undefined = false) =>
  isSlow ? t`Still loading…` : t`Loading…`;

const getDefaultAppInitFunctions = (): (() => void)[] => [];

export const PLUGIN_APP_INIT_FUNCTIONS = definePluginSlot(
  getDefaultAppInitFunctions,
);

const getDefaultLandingPage = () => ({
  getLandingPage: () => "/",
});

export const PLUGIN_LANDING_PAGE: {
  getLandingPage: () => string | null | undefined;
} = definePluginSlot(getDefaultLandingPage);

const getDefaultHomepageSetting = () => ({
  CustomUrlOption: null,
});

export const PLUGIN_HOMEPAGE_SETTING: {
  CustomUrlOption: { label: string; Control: ComponentType } | null;
} = definePluginSlot(getDefaultHomepageSetting);

// dispatch is typed as thunk-capable so EE middlewares can dispatch async thunks
const getDefaultReduxMiddlewares = (): Middleware<
  Record<string, never>,
  State,
  ThunkDispatch<State, unknown, Action>
>[] => [];

export const PLUGIN_REDUX_MIDDLEWARES = definePluginSlot(
  getDefaultReduxMiddlewares,
);

const getDefaultLogoIconComponents = (): ComponentType[] => [];

export const PLUGIN_LOGO_ICON_COMPONENTS = definePluginSlot(
  getDefaultLogoIconComponents,
);

const getDefaultAdminAllowedPathGetters = (): ((
  user: any,
) => AdminPathKey[])[] => [];

export const PLUGIN_ADMIN_ALLOWED_PATH_GETTERS = definePluginSlot(
  getDefaultAdminAllowedPathGetters,
);

const getDefaultSelectors = () => ({
  // SALDP: white-labelling is built into this fork.
  canWhitelabel: (_state: State) => true,
  getLoadingMessageFactory: (_state: State) => getLoadingMessage,
  getIsWhiteLabeling: (_state: State) => true,
  getApplicationName: getBrandName,
  getShowMetabaseLinks,
  // SALDP: no Metabase login or home-page art.
  getLoginPageIllustration: (_state: State): IllustrationValue => null,
  getLandingPageIllustration: (_state: State): IllustrationValue => null,
  getNoDataIllustration: (_state: State): string | null => {
    return noResultsSource;
  },
  getNoObjectIllustration: (_state: State): string | null => {
    return noResultsSource;
  },
});

export const PLUGIN_SELECTORS = definePluginSlot(getDefaultSelectors);

const getDefaultFormWidgets = (): Record<string, ComponentType<any>> => ({});

export const PLUGIN_FORM_WIDGETS = definePluginSlot(getDefaultFormWidgets);

const getDefaultSnippetSidebarPlusMenuOptions = (): ((
  snippetSidebar: SnippetSidebarContext,
) => SnippetSidebarMenuOption)[] => [];
const getDefaultSnippetSidebarRowRenderers =
  (): SnippetSidebarRowRenderers => ({
    collection: null,
  });
const getDefaultSnippetSidebarHeaderButtons = (): ((
  snippetSidebar: SnippetSidebarContext,
  opts: { className?: string },
) => ReactNode)[] => [];

export const PLUGIN_SNIPPET_SIDEBAR_PLUS_MENU_OPTIONS = definePluginSlot(
  getDefaultSnippetSidebarPlusMenuOptions,
);
export const PLUGIN_SNIPPET_SIDEBAR_ROW_RENDERERS = definePluginSlot(
  getDefaultSnippetSidebarRowRenderers,
);
export const PLUGIN_SNIPPET_SIDEBAR_HEADER_BUTTONS = definePluginSlot(
  getDefaultSnippetSidebarHeaderButtons,
);

const getDefaultDashboardSubscriptionParametersSectionOverride =
  (): PluginDashboardSubscriptionParametersSectionOverride => ({
    Component: undefined,
  });

export const PLUGIN_DASHBOARD_SUBSCRIPTION_PARAMETERS_SECTION_OVERRIDE =
  definePluginSlot(getDefaultDashboardSubscriptionParametersSectionOverride);

const getDefaultReducers = () => ({
  advancedPermissionsPlugin: () => null,
  applicationPermissionsPlugin: () => null,
  sandboxingPlugin: () => null,
  shared: () => null,
  documents: () => null,
  remoteSyncPlugin: () => null,
});

export const PLUGIN_REDUCERS: {
  advancedPermissionsPlugin: any;
  applicationPermissionsPlugin: any;
  sandboxingPlugin: any;
  shared: any;
  documents: any;
  remoteSyncPlugin: any;
} = definePluginSlot(getDefaultReducers);

const getDefaultIsEeBuild = () => ({
  isEEBuild: () => false,
});

export const PLUGIN_IS_EE_BUILD = definePluginSlot(getDefaultIsEeBuild);
